// @ts-strict-ignore
import * as React from 'react';

import type {
  BoardContextType,
  KeywordContextType,
  KeywordType,
  SourceType,
  ToggleFilterType,
} from '@/features/CustomFeedV2/common';
import type { AIFeedNavigationType } from '@/features/CustomFeedV2/components/FeedTypeModal/hooks/useFeedOptionNavigation';
import type { CommonFilterType, Filter } from '@/types';
import { capitalize } from '@/utils/capitalize';
import { getUniqueObjectsArray } from '@/utils/getUniqueObjectsArray';
import { getEntries } from '@/utils/object';

export const useCustomFeedState = (initialValue: CustomFeedState) => {
  return React.useReducer(customFeedReducer, initialValue);
};

const customFeedReducer = (
  state: CustomFeedState,
  action: CustomFeedActions
): CustomFeedState => {
  switch (action.type) {
    case 'SET_FEED_NAME':
      return {
        ...state,
        board: {
          ...state.board,
          name: action.payload.name,
        },
      };
    case 'SET_FEED_DESCRIPTION':
      return {
        ...state,
        board: {
          ...state.board,
          description: action.payload.description,
        },
      };
    case 'ADD_INCLUDE_KEYWORDS': {
      return {
        ...state,
        board: {
          ...state.board,
          keywords: {
            ...state.board.keywords,
            include: getUpdatedKeywords(
              state.board.keywords,
              action.payload.keywords,
              'include'
            ),
          },
        },
      };
    }
    case 'ADD_EXCLUDE_KEYWORDS': {
      return {
        ...state,
        board: {
          ...state.board,
          keywords: {
            ...state.board.keywords,
            exclude: getUpdatedKeywords(
              state.board.keywords,
              action.payload.keywords,
              'exclude'
            ),
          },
        },
      };
    }
    case 'REMOVE_KEYWORDS': {
      let newState: CustomFeedState;

      if (action.payload.type) {
        newState = {
          ...state,
          board: {
            ...state.board,
            keywords: {
              ...state.board.keywords,
              [action.payload.type]: removeKeywords(
                state.board.keywords[action.payload.type],
                action.payload.keywords
              ),
            },
          },
        };
      } else {
        newState = {
          ...state,
          board: {
            ...state.board,
            keywords: {
              include: removeKeywords(
                state.board.keywords.include,
                action.payload.keywords
              ),
              exclude: removeKeywords(
                state.board.keywords.exclude,
                action.payload.keywords
              ),
            },
          },
        };
      }

      const activeKeywordType = state.activeKeywordType;
      const includedKeywordsCount = newState.board.keywords.include.length;
      const excludedKeywordsCount = newState.board.keywords.exclude.length;

      if (
        (activeKeywordType === 'include' && includedKeywordsCount === 0) ||
        (activeKeywordType === 'exclude' && excludedKeywordsCount === 0)
      ) {
        newState = {
          ...newState,
          activeKeywordType: 'all',
        };
      }

      return newState;
    }
    case 'EDIT_KEYWORD': {
      const { keywordToUpdate, updatedKeyword, type } = action.payload;

      return {
        ...state,
        board: {
          ...state.board,
          keywords: {
            ...state.board.keywords,
            [type]: state.board.keywords[type].reduce((acc, keyword) => {
              if (keyword.keyword === keywordToUpdate) {
                return [
                  ...acc,
                  {
                    keyword: updatedKeyword,
                    valid: true,
                  },
                ];
              }

              return [...acc, keyword] as KeywordType[];
            }, [] as KeywordType[]),
          },
        },
      };
    }
    case 'UPDATE_KEYWORDS': {
      const { updatedKeywords } = action.payload;

      return {
        ...state,
        board: {
          ...state.board,
          keywords: updatedKeywords,
        },
      };
    }
    case 'SET_ACTIVE_KEYWORD_TYPE': {
      return {
        ...state,
        activeKeywordType: action.payload.activeKeywordType,
      };
    }
    case 'ADD_SOURCES': {
      return {
        ...state,
        board: {
          ...state.board,
          sources: {
            ...state.board.sources,
            ...action.payload.sources,
          },
        },
      };
    }
    case 'REMOVE_SOURCES': {
      const sourceKeysToRemove = action.payload.sources;
      const updatedSources = Object.fromEntries(
        getEntries(state.board.sources).filter(
          ([sourceKey]) => !sourceKeysToRemove.includes(sourceKey)
        )
      );

      return {
        ...state,
        board: {
          ...state.board,
          sources: updatedSources,
        },
      };
    }
    case 'SOURCE_FILTER': {
      const { categoryKey, sourceKey, filterName, filterItems, filterMode } =
        action.payload;

      const isCommonFilter = sourceKey === 'common';

      let sourceObj: CommonFilterType | SourceType =
        state.board.sources[sourceKey];
      if (isCommonFilter) {
        sourceObj = state?.board?.common?.[categoryKey as string] || {};
      }

      sourceObj.filter = sourceObj.filter || {};
      sourceObj.exclusion_filter = sourceObj.exclusion_filter || {};

      const targetField =
        filterMode === 'include' ? 'filter' : 'exclusion_filter';
      const otherField =
        filterMode === 'include' ? 'exclusion_filter' : 'filter';

      if (filterName in (sourceObj[otherField] ?? {})) {
        delete sourceObj?.[otherField]?.[filterName];
      }

      if (filterItems.length === 0) {
        delete sourceObj?.[targetField]?.[filterName];
        return { ...state };
      }

      if (isCommonFilter) {
        return {
          ...state,
          board: {
            ...state.board,
            common: {
              ...state.board.common,
              [categoryKey as string]: {
                ...state?.board?.common?.[categoryKey as string],
                [targetField]: {
                  ...state?.board?.common?.[categoryKey as string]?.[
                    targetField
                  ],
                  [filterName]: filterItems,
                },
              },
            },
          },
        };
      }

      return {
        ...state,
        board: {
          ...state.board,
          sources: {
            ...state.board.sources,
            [sourceKey]: {
              ...state.board.sources?.[sourceKey],
              [targetField]: {
                ...state.board.sources?.[sourceKey]?.[targetField],
                [filterName]: filterItems,
              },
            },
          },
        },
      };
    }
    case 'SET_DATE_RANGE': {
      const { dateRange } = action.payload;
      return {
        ...state,
        board: {
          ...state.board,
          date_range: dateRange,
        },
      };
    }
    case 'SET_COMPANIES': {
      const { companies } = action.payload;
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          companies,
        } as AISmartsType,
        board: {
          ...state.board,
          name: capitalize(companies?.[0]?.label as string),
        },
      };
    }
    case 'SET_ATTRIBUTES': {
      const { attributes } = action.payload;
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          attributes,
        } as AISmartsType,
      };
    }
    case 'ADD_TOPIC': {
      const { topic, currentTopic } = action.payload;
      const newTopics = [
        ...new Set([...(state.aiSmarts?.topics || []), topic]),
      ];
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          ...(currentTopic && { currentTopic }),
          topics: newTopics,
        } as AISmartsType,
        board: {
          ...state.board,
          name: capitalize(
            state.board.name || (newTopics?.length === 1 ? newTopics?.[0] : '')
          ),
        },
      };
    }
    case 'REMOVE_TOPIC': {
      const { topic } = action.payload;
      const newTopics = state.aiSmarts?.topics?.filter((t) => t !== topic);
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          topics: newTopics,
        } as AISmartsType,
      };
    }
    case 'REMOVE_ALL_TOPICS': {
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          topics: undefined,
        } as AISmartsType,
        board: {
          ...state.board,
          name: '',
        },
      };
    }
    case 'OPEN_MODAL': {
      return { ...state, modal: action.payload };
    }
    case 'CLOSE_MODAL': {
      return { ...state, modal: undefined };
    }
    case 'REMOVE_SOURCE_EXCLUDE_KEYWORDS': {
      const { source, keywords } = action.payload;

      return {
        ...state,
        board: {
          ...state.board,
          sources: {
            ...state.board.sources,
            [source]: {
              ...state.board.sources[source],
              exclusion_rules: {
                ...state.board.sources[source].exclusion_rules,
                keywords: state.board.sources[
                  source
                ]?.exclusion_rules?.keywords.filter(
                  (keyword) => !keywords.includes(keyword)
                ),
              },
            },
          } as Record<string, SourceType>,
        },
      };
    }
    case 'SET_AI_FEED_TYPE': {
      return {
        ...state,
        aiSmarts: {
          ...state.aiSmarts,
          type: action.payload.type,
        } as AISmartsType,
      };
    }
    default:
      throw new Error('from CustomFeedContext, I should not be here');
  }
};

const getUpdatedKeywords = (
  keywords: KeywordContextType,
  keywordsToAdd: KeywordType[],
  filterType: ToggleFilterType
) => {
  const existingKeywords = keywords[
    filterType === 'include' ? 'exclude' : 'include'
  ].map((keyword) => keyword.keyword);

  const filteredKeywords = keywordsToAdd.filter(
    (keyword) => !existingKeywords.includes(keyword.keyword)
  );

  const updatedKeywords = [...keywords[filterType], ...filteredKeywords];
  return getUniqueObjectsArray(updatedKeywords, 'keyword');
};

const removeKeywords = (
  keywords: KeywordType[],
  keywordsToRemove: string[]
) => {
  return keywords.filter(
    (keyword) => !keywordsToRemove.includes(keyword.keyword)
  );
};

export type ActiveKeywordType = ToggleFilterType | 'all';

export type AISmartsType = {
  isOptionsPage?: boolean;
  companies?: Filter[];
  attributes?: Filter[];
  topics?: string[];
  currentTopic?: string;
  type?: AIFeedNavigationType;
};

export type CustomFeedState = {
  board: BoardContextType;
  aiSmarts?: AISmartsType;
  modal?: Modal;
  activeKeywordType?: ActiveKeywordType;
};

export type Modal =
  | {
      type: 'ADD_NEW_SOURCES';
      payload: null;
    }
  | {
      type: 'EDIT_KEYWORD';
      payload: {
        keyword: string;
        type: ToggleFilterType;
      };
    };

export type CustomFeedActions =
  | {
      type: 'ADD_EXCLUDE_KEYWORDS';
      payload: { keywords: KeywordType[] };
    }
  | {
      type: 'ADD_INCLUDE_KEYWORDS';
      payload: { keywords: KeywordType[] };
    }
  | {
      type: 'ADD_SOURCES';
      payload: {
        sources: Record<string, SourceType>;
      };
    }
  | {
      type: 'ADD_TOPIC';
      payload: {
        topic: string;
        currentTopic?: string;
      };
    }
  | {
      type: 'CLOSE_MODAL';
    }
  | {
      type: 'EDIT_KEYWORD';
      payload: {
        keywordToUpdate: string;
        updatedKeyword: string;
        type: ToggleFilterType;
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: Modal;
    }
  | {
      type: 'REMOVE_ALL_TOPICS';
      payload: null;
    }
  | {
      type: 'REMOVE_KEYWORDS';
      payload: {
        keywords: string[];
        type?: ToggleFilterType;
      };
    }
  | {
      type: 'REMOVE_SOURCE_EXCLUDE_KEYWORDS';
      payload: {
        source: string;
        keywords: string[];
      };
    }
  | {
      type: 'REMOVE_SOURCES';
      payload: {
        sources: string[];
      };
    }
  | {
      type: 'REMOVE_TOPIC';
      payload: {
        topic: string;
      };
    }
  | {
      type: 'SET_ACTIVE_KEYWORD_TYPE';
      payload: {
        activeKeywordType: ActiveKeywordType;
      };
    }
  | {
      type: 'SET_AI_FEED_TYPE';
      payload: {
        type: AIFeedNavigationType;
      };
    }
  | {
      type: 'SET_ATTRIBUTES';
      payload: {
        attributes: Filter[];
      };
    }
  | {
      type: 'SET_COMPANIES';
      payload: {
        companies: Filter[];
      };
    }
  | {
      type: 'SET_DATE_RANGE';
      payload: {
        dateRange: string;
      };
    }
  | {
      type: 'SET_FEED_DESCRIPTION';
      payload: { description: string };
    }
  | {
      type: 'SET_FEED_NAME';
      payload: { name: string };
    }
  | {
      type: 'SOURCE_FILTER';
      payload: {
        sourceKey: string;
        categoryKey?: string;
        filterName: string;
        filterItems: string[];
        filterMode: ToggleFilterType;
      };
    }
  | {
      type: 'UPDATE_KEYWORDS';
      payload: {
        updatedKeywords: BoardContextType['keywords'];
      };
    };
